import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { config } from '../config';
import { identifyUser, resetPostHog, trackEvent } from '../services/posthogService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // Check for JWT token first
      const token = authService.getAccessToken();
      if (token) {
        // Verify token with backend (using current-user endpoint which now supports JWT via middleware)
        const response = await authService.getCurrentUser();
        if (response.data.isAuthenticated) {
          setUser(response.data.user);
          setIsAuthenticated(true);
          identifyUser(response.data.user);
          trackEvent('login', { method: 'jwt', role: response.data.user.role });
          setLoading(false);
          return;
        }
      }

      // Fallback to session check (Microsoft Auth)
      const response = await authService.getCurrentUser();
      if (response.data.isAuthenticated) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        identifyUser(response.data.user);
        trackEvent('login', { method: 'microsoft', role: response.data.user.role });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // If JWT headers were sent and failed, clear tokens
      if (authService.getAccessToken()) {
        authService.clearTokens();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (tenantKey) => {
    const tenantParam = tenantKey ? `?tenant=${tenantKey}` : '';
    window.location.href = `${config.BACKEND_URL}/api/auth/microsoft${tenantParam}`;
  };

  const loginWithCredentials = async (email, password) => {
    try {
      const response = await authService.jwtLogin(email, password);
      // jwtLogin sets tokens in localStorage

      // Get user info from token or fetch profile
      // For simplicity, we can decode the token or fetch current-user
      const userResponse = await authService.getCurrentUser();

      if (userResponse.data.isAuthenticated) {
        setUser(userResponse.data.user);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error('JWT Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Check if we are using JWT
      if (authService.getAccessToken()) {
        await authService.jwtLogout();
      } else {
        await authService.logout();
      }

      setUser(null);
      setIsAuthenticated(false);
      trackEvent('logout');
      resetPostHog();

      // If it was Microsoft auth, we might want to redirect to login
      // If it was JWT, we just clear state and redirect
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout failed:', error);
      // Force local logout anyway
      authService.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      window.location.href = '/login';
    }
  };

  const hasRole = (requiredRoles) => {
    if (!user || !user.role) return false;
    return requiredRoles.includes(user.role);
  };

  // Check if user can edit Main data (Admin, Editor)
  const canEditMain = () => {
    if (!user || !user.role) return false;
    return ['Admin', 'Editor'].includes(user.role);
  };

  // Check if user can edit Branch data (Admin, Editor Branch)
  const canEditBranch = () => {
    if (!user || !user.role) return false;
    return ['Admin', 'Editor Branch'].includes(user.role);
  };

  // Check if user is Admin
  const isAdmin = () => {
    return user?.role === 'Admin';
  };

  // Check if user is Editor Branch
  const isEditorBranch = () => {
    return user?.role === 'Editor Branch';
  };

  // Check if user is regular Editor (not Branch)
  const isEditor = () => {
    return user?.role === 'Editor';
  };

  // Check if user is User Branch
  const isUserBranch = () => {
    return user?.role === 'User Branch';
  };

  // Check if user can view Main data (User Branch cannot)
  const canViewMain = () => {
    if (!user || !user.role) return false;
    return user.role !== 'User Branch';
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    loginWithCredentials,
    logout,
    hasRole,
    canEditMain,
    canEditBranch,
    canViewMain,
    isAdmin,
    isEditor,
    isEditorBranch,
    isUserBranch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};